import { _ } from "../../core/polyglot/polyglot.js";
import { isInSmallScreen } from "../../core/helpers/screen-util.js";
import AccountingDocumentState from "../shared/documentInfrastructure/AccountingDocumentState.js";
import ForecastSeriesComputationRequest from "../../models/forecast/instance/ForecastSeriesComputationRequest.js";
import ForecastTemplateInterpreter from "../../models/forecast/interpretation/ForecastTemplateInterpreter.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import RuleTemplateInheritingCacheManager from "../../models/forecast/template/RuleTemplateInheritingCacheManager.js";
import SeriesSpecificationBuilder from "../../models/forecast/instance/SeriesSpecificationBuilder.js";

export const settingsTabId = "settings";

const configuration = {
	forcedIsSmallScreenValue: undefined,
};

/**
 * Configuration for `ReportDisplaySettings`.
 *
 * @param {boolean} [forcedIsSmallScreenValue] - When defined, overwrite the
 *   computation result in `isInSmallScreen`
 */
export function setConfiguration({ forcedIsSmallScreenValue } = {}) {
	configuration.forcedIsSmallScreenValue = forcedIsSmallScreenValue;
}

/**
 * Hold and manage the state for the ForecastEditorDocument.
 *
 * @param {boolean} [saveSpaceMode] - When true, the view tries to maximize
 *   the number of report rows on screen
 */
export default class ForecastEditorDocumentState extends AccountingDocumentState {
	constructor({
		saveSpaceMode = isInSmallScreen({ configuration }),
		period,
	} = {}) {
		super(...arguments);

		this._forecastTemplate = undefined;
		this._interpretationContext = undefined;
		this._forecast = undefined;
		this._budgets = [];

		// remembers which one of the forecast template scenarios is currently selected by the user
		this._currentScenario = undefined;

		// remembers how rule templates are inherited for all reports
		// and all scenarios:
		this._ruleTemplateInheritingCacheManager = undefined;

		this._isInSaveSpaceMode = saveSpaceMode;
		this._period = period;
	}

	isInSaveSpaceMode() {
		return this._isInSaveSpaceMode;
	}

	setSaveSpaceMode(value) {
		this._isInSaveSpaceMode = value;
	}

	getBudgets() {
		return this._budgets;
	}

	getVatRates() {
		return this._vatSettings.getVatRates();
	}

	getForecast() {
		return this._forecast;
	}

	getIncomeStatement() {
		return this._forecast.getIncomeStatement();
	}

	getBalanceSheet() {
		return this._forecast.getBalanceSheet();
	}

	getCashFlow() {
		return this._forecast.getCashFlow();
	}

	getReports() {
		return this._forecast.getReports();
	}

	getCurrentReport() {
		return this._currentReport;
	}

	setCurrentReport(report) {
		this._currentReport = report;
		this._setCurrentTabParameter(this._getCurrentTabIdForReport(report));
	}

	getForecastTemplateName() {
		return this._forecastTemplate?.getName();
	}

	getReportsAsList() {
		return this._forecast.getReportsAsList();
	}

	getReportDisplaySettings() {
		return this.getReportsAsList().map((report) =>
			report.getDisplaySettings(),
		);
	}

	getReportFromId(reportId) {
		return this._forecast.getReportFromId(reportId);
	}

	getAllAccounts() {
		return this._interpretationContext.getAllAccounts();
	}

	getConfiguration() {
		return this._forecastTemplate.getConfiguration();
	}

	getForecastTemplateDescription() {
		return this.getConfiguration().getForecastTemplateDescription();
	}

	getNumberOfReferencePeriods() {
		return this.getConfiguration().getNumberOfReferencePeriods();
	}

	/**
	 * Return existing `RuleTemplate` for a specified cell and a rule model class.
	 *
	 * @param {AbstractRow} row - The vertical position of the cell.
	 * @param {AbstractColumn} column - The horizontal position of the cell.
	 * @param {Class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 *   filter by this rule model class
	 */
	getOwnRuleTemplateFor({ row, column, ruleModelClass }) {
		return this._currentScenario.getOwnRuleTemplateFor({
			rowId: row.getStableId(),
			start: column.getStart(),
			ruleModelClass,
		});
	}

	getManualJournalEntriesFor({ row, column }) {
		return this._currentScenario.getAllManualJournalEntryRuleTemplatesFor({
			accountNumber: row.getAccount().getNumber(),
			start: column.getStart(),
		});
	}

	getManualEntriesFor({ row, column }) {
		return this._currentScenario.getAllManualEntryRuleTemplatesFor({
			rowId: row.getStableId(),
			start: column.getStart(),
		});
	}

	getRuleTemplateSource() {
		return this._service.getRuleTemplateSource({
			forecastTemplateId: this._forecastTemplate.getId(),
		});
	}

	getRowForAccountNumber(accountNumber) {
		return this._forecast.getRowForAccountNumber(accountNumber);
	}

	/**
	 * Return ruleTemplate inherited by a specified cell and for a rule model class.
	 *
	 * @param {AbstractRow} row - The vertical position of the cell.
	 * @param {AbstractColumn} column - The horizontal position of the cell.
	 * @param {Class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 * filter by this rule model class.
	 */
	getInheritedRuleTemplateFor({ row, column, ruleModelClass }) {
		return this._ruleTemplateInheritingCacheManager.getInheritedRuleTemplateFor(
			{
				row,
				column,
				ruleModelClass,
				scenario: this._currentScenario,
				forecastReport: this._currentReport,
			},
		);
	}

	getCurrentScenario() {
		return this._currentScenario;
	}

	getFirstReport() {
		return this._forecast.getFirstReport();
	}

	getCurrentPeriod() {
		return this.getFirstReport().getCurrentPeriod();
	}

	async setCurrentPeriod(period) {
		const defaultPeriod = period || this._period;
		this._interpretationContext.setPeriod(defaultPeriod);

		this._forecastTemplate.setFrozenCurrentPeriod(period);
		this._forecast = this._interpret();

		await Promise.all([
			this.updateForecastTemplateConfiguration(),
			this._computeForecast(),
		]);

		this._onForecastPeriodUpdated(defaultPeriod);
	}

	async bulkEditTemplates(bulkAction) {
		for (const ruleTemplate of bulkAction.getRuleTemplatesToAdd()) {
			// eslint-disable-next-line no-await-in-loop -- We WANT to not parallelize those call yet
			await this._addNewRuleTemplate(ruleTemplate);
		}
		for (const ruleTemplate of bulkAction.getRuleTemplatesToUpdate()) {
			// eslint-disable-next-line no-await-in-loop -- We WANT to not parallelize those call yet
			await this._updateRuleTemplate(ruleTemplate);
		}
		for (const ruleTemplate of bulkAction.getRuleTemplatesToRemove()) {
			// eslint-disable-next-line no-await-in-loop -- We WANT to not parallelize those call yet
			await this._removeRuleTemplate(ruleTemplate);
		}

		notifier.success(_("Rule templates edited successfully"));

		await this._computeForecast();
		this._onForecastReportUpdated();
	}

	async computeReport({ report }) {
		const builder = this._makeSeriesSpecificationBuilder();

		const computationResult =
			await this._service.computeForecastSeriesSpecifications({
				computationRequest: this._makeForecastSeriesComputationRequest({
					seriesSpecifications:
						report.makeComputationSeriesSpecifications({ builder }),
				}),
				forecastTemplateId: this._forecast.getForecastTemplateId(),
			});

		report.setDataFromSeries(computationResult);

		// if the report is active after fetching the data
		if (this.getCurrentReport() === report) {
			this._onForecastReportUpdated();
		}
	}

	setCurrentReportFromCurrentTab(currentTab) {
		this._currentReport = this._getReportFromCurrentTabId(currentTab);
	}

	publishForecast({ publicationRequest }) {
		const seriesSpecifications =
			this._forecast.makeComputationSeriesSpecificationsForPublication({
				builder: this._makeSeriesSpecificationBuilder(),
			});

		return this._service.publishForecast({
			computationRequest: this._makeForecastSeriesComputationRequest({
				seriesSpecifications,
				description: publicationRequest.getDescription(),
				ignoredReportIds: publicationRequest.asIgnoredReports(),
			}),
			name: publicationRequest.getName(),
			forecast: this._forecast,
		});
	}

	setForecastTemplateDescription(description) {
		this.getConfiguration().setForecastTemplateDescription(description);
		return this.updateForecastTemplateConfiguration({ notify: false });
	}

	updateForecastTemplateConfiguration({ notify } = {}) {
		return this._service.updateForecastTemplateConfiguration({
			forecastTemplateConfiguration: this.getConfiguration(),
			forecastTemplateId: this._forecastTemplate.getId(),
			notify,
		});
	}

	_getCurrentTabIdForReport(report) {
		if (!report) return settingsTabId;

		return report.getId();
	}

	async _addNewRuleTemplate(ruleTemplate) {
		const forecastTemplateId = this._forecastTemplate.getId();
		ruleTemplate.setForecastTemplateId(forecastTemplateId);

		const savedRuleTemplate = await this._service.createRuleTemplate({
			ruleTemplate,
			forecastTemplateId,
			notify: false,
		});

		this._currentScenario.addRuleTemplate(savedRuleTemplate);

		this._ruleTemplateInheritingCacheManager.addRuleTemplate({
			ruleTemplate: savedRuleTemplate,
			scenario: this._currentScenario,
			forecastReport: this._currentReport,
		});
	}

	async _updateRuleTemplate(ruleTemplate) {
		const forecastTemplateId = this._forecastTemplate.getId();
		ruleTemplate.setForecastTemplateId(forecastTemplateId);

		await this._service.updateRuleTemplate({
			ruleTemplate,
			forecastTemplateId,
			notify: false,
		});
	}

	async _removeRuleTemplate(ruleTemplate) {
		const forecastTemplateId = this._forecastTemplate.getId();
		const ruleTemplateId = ruleTemplate.getId();

		await this._service.removeRuleTemplate({
			ruleTemplateId,
			forecastTemplateId,
			notify: false,
		});

		this._currentScenario.removeRuleTemplate(ruleTemplate);

		this._ruleTemplateInheritingCacheManager.removeRuleTemplate({
			forecastReport: this._currentReport,
			scenario: this._currentScenario,
			ruleTemplate,
		});
	}

	_makeSeriesSpecificationBuilder() {
		const keyFigures = this._interpretationContext?.getKeyFigures();
		return new SeriesSpecificationBuilder({ keyFigures });
	}

	async _setupInitialData({
		forecastTemplate,
		interpretationContext,
		budgets,
		vatSettings,
		onForecastReportUpdated,
		onForecastPeriodUpdated,
		currentTabId,
		setCurrentTabParameter = () => {},
	}) {
		this._ruleTemplateInheritingCacheManager =
			new RuleTemplateInheritingCacheManager();

		this._forecastTemplate = forecastTemplate;
		this._interpretationContext = interpretationContext;
		this._budgets = budgets;
		this._vatSettings = vatSettings;
		this._currentScenario = forecastTemplate.getBaselineScenario();
		this._onForecastReportUpdated = onForecastReportUpdated;
		this._onForecastPeriodUpdated = onForecastPeriodUpdated;
		this._forecast = this._interpret();

		await this._computeForecast();
		this._setCurrentTabParameter = setCurrentTabParameter;
		this.setCurrentReportFromCurrentTab(currentTabId);
	}

	_getReportFromCurrentTabId(currentTabId) {
		if (currentTabId === settingsTabId) {
			return null;
		}

		const correspondingReport = this.getReportsAsList().find(
			(report) => report.getId() === currentTabId,
		);

		return correspondingReport || this.getFirstReport();
	}

	async _computeForecast() {
		const seriesSpecifications =
			this._forecast.makeComputationSeriesSpecifications({
				builder: this._makeSeriesSpecificationBuilder(),
			});

		const computationResult =
			await this._service.computeForecastSeriesSpecifications({
				computationRequest: this._makeForecastSeriesComputationRequest({
					seriesSpecifications,
				}),
				forecastTemplateId: this._forecast.getForecastTemplateId(),
			});

		this._forecast.setDataFromSeries(computationResult);
	}

	_interpret() {
		return ForecastTemplateInterpreter.interpret({
			forecastTemplate: this._forecastTemplate,
			interpretationContext: this._interpretationContext,
		});
	}

	_makeForecastSeriesComputationRequest({
		seriesSpecifications = [],
		description,
		ignoredReportIds = [],
	}) {
		const computationRequest = new ForecastSeriesComputationRequest({
			forecast: this._forecast,
			description,
			ignoredReportIds,
		});

		computationRequest.addRuleTemplates(
			this._currentScenario.getRuleTemplates(),
		);

		computationRequest.addSeriesSpecifications(seriesSpecifications);

		return computationRequest;
	}
}
